import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, onSnapshot, getCountFromServer } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import Enquiries from './Enquiries'; 
import AddServices from './AddServices';
import Items from './Items';
import Orders from './Orders';
import ExpressOrders from './ExpressOrders';
import ListUsers from './ListUsers';
import ListAgents from './ListAgents';
import PaymentRequests from './PaymentRequests'; 
import AgentOutstanding from './AgentOutstanding'; // 🎯 Imported AgentOutstanding component
import AccountsDashboard from './accounts/AccountsDashboard'; 
import logo from '../assets/logo/logo.png'; 
import '../App.css';

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const navigate = useNavigate();

  // 🎯 Fetch Admin User Info & Role from LocalStorage
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('adminUser');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setCurrentUser(parsedUser);
      } catch (e) {
        console.error("Error parsing stored admin user:", e);
      }
    }
  }, []);

  // Performance Optimization: Main state lifted up to load once
  const [orders, setOrders] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [counts, setCounts] = useState({
    users: 0,
    agents: 0,
    services: 0,
    items: 0,
    enquiries: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Single realtime listener for express orders
    const unsubOrders = onSnapshot(collection(db, 'expressOrder'), (snap) => {
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOrders(data);
    }, (error) => console.error("Orders listener error:", error));

    // Single realtime listener for payment requests
    const unsubPayments = onSnapshot(collection(db, 'paymentRequests'), (snap) => {
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPaymentRequests(data);
    }, (error) => console.error("Payments listener error:", error));

    // Fast Server-side collection counts
    const fetchCounts = async () => {
      try {
        const [usersSnap, agentsSnap, servicesSnap, itemsSnap, enquiriesSnap] = await Promise.all([
          getCountFromServer(collection(db, 'users')),
          getCountFromServer(collection(db, 'agents')),
          getCountFromServer(collection(db, 'services')),
          getCountFromServer(collection(db, 'items')),
          getCountFromServer(collection(db, 'contacts'))
        ]);

        setCounts({
          users: usersSnap.data().count,
          agents: agentsSnap.data().count,
          services: servicesSnap.data().count,
          items: itemsSnap.data().count,
          enquiries: enquiriesSnap.data().count
        });
      } catch (error) {
        console.error("Error fetching optimized counts: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCounts();

    return () => {
      unsubOrders();
      unsubPayments();
    };
  }, []);

  const handleLogout = async () => {
    localStorage.removeItem('adminUser'); // Clear user session details
    await signOut(auth);
    navigate('/admin-login');
  };

  // Check if current user is Super Admin
  const isSuperAdmin = currentUser?.role === 'super_admin';

  return (
    <div className="admin-container">
      {/* 🎯 SIDEBAR NAVIGATION MENU WITH SCROLLABLE CONTAINER */}
      <aside 
        className="admin-sidebar"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          maxHeight: '100vh',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}
      >
        <div className="sidebar-logo" style={{ flexShrink: 0 }}>
          <img src={logo} alt="Mr White Logo" />
        </div>
        
        {/* 🎯 Scrollable Navigation Links */}
        <nav 
          className="sidebar-nav"
          style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            paddingRight: '4px'
          }}
        >
          <div 
            className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`} 
            onClick={() => setActiveTab('dashboard')}
          >
            <i className="fas fa-th-large"></i> <span>Dashboard</span>
          </div>

          <div 
            className={`nav-item ${activeTab === 'enquiries' ? 'active' : ''}`} 
            onClick={() => setActiveTab('enquiries')}
          >
            <i className="fas fa-envelope"></i> <span>Enquiries</span>
          </div>

          <div 
            className={`nav-item ${activeTab === 'services' ? 'active' : ''}`} 
            onClick={() => setActiveTab('services')}
          >
            <i className="fas fa-tshirt"></i> <span>Services</span>
          </div>

          <div 
            className={`nav-item ${activeTab === 'items' ? 'active' : ''}`} 
            onClick={() => setActiveTab('items')}
          >
            <i className="fas fa-list-ul"></i> <span>Items</span>
          </div>

          <div
            className={`nav-item ${activeTab === 'expressOrders' ? 'active' : ''}`}
            onClick={() => setActiveTab('expressOrders')}
          >
            <i className="fas fa-bolt"></i> <span>Laundry Orders</span>
          </div>

          <div 
            className={`nav-item ${activeTab === 'customers' ? 'active' : ''}`} 
            onClick={() => setActiveTab('customers')}
          >
            <i className="fas fa-users"></i> <span>Customers</span>
          </div>

          <div
            className={`nav-item ${activeTab === 'agents' ? 'active' : ''}`}
            onClick={() => setActiveTab('agents')}
          >
            <i className="fas fa-user-tie"></i> <span>Agents</span>
          </div>
          
          <div
            className={`nav-item ${activeTab === 'paymentRequests' ? 'active' : ''}`}
            onClick={() => setActiveTab('paymentRequests')}
          >
            <i className="fas fa-money-check-alt"></i> <span>Payment Requests</span>
          </div>

          <div
            className={`nav-item ${activeTab === 'agentInvoices' ? 'active' : ''}`}
            onClick={() => setActiveTab('agentInvoices')}
          >
            <i className="fas fa-file-invoice-dollar"></i> <span>Agent's Invoices</span>
          </div>
        </nav>

        {/* 🎯 Fixed Logout Button at Bottom */}
        <button 
          onClick={handleLogout} 
          className="sidebar-logout"
          style={{ flexShrink: 0, marginTop: '12px' }}
        >
          <i className="fas fa-sign-out-alt"></i> <span>Logout</span>
        </button>
      </aside>

      {/* MAIN DASHBOARD CONTENT */}
      <main className="admin-main">
        <header className="admin-topbar">
          <h1>
            {activeTab === 'dashboard' && "Dashboard Overview"}
            {activeTab === 'enquiries' && "Customer Enquiries"}
            {activeTab === 'services' && "Manage Services"}
            {activeTab === 'items' && "Manage items"}
            {activeTab === 'orders' && "Order Management"}
            {activeTab === 'expressOrders' && "Express Order Management"}
            {activeTab === 'customers' && "Customer Database"}
            {activeTab === 'agents' && "Agent Management"}
            {activeTab === 'paymentRequests' && "Agent Payment Requests"}
            {activeTab === 'agentInvoices' && "Agent Invoices & Outstanding"}
            {activeTab === 'accounts' && "Accounts & Billing Portal"}
          </h1>

          <div className="topbar-actions" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Accounts Navigation Button (Only visible for Super Admin) */}
            {isSuperAdmin && (
              <button 
                onClick={() => navigate('/admin/accounts')}
                className={`btn-accounts-nav ${activeTab === 'accounts' ? 'active-accounts' : ''}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  backgroundColor: activeTab === 'accounts' ? '#16a34a' : '#2563eb',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '13px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)'
                }}
              >
                <i className="fas fa-calculator"></i> Accounts
              </button>
            )}

            <div className="admin-profile">
              <span>Welcome, {currentUser?.name || 'Admin'} ({currentUser?.role === 'sub_admin' ? 'Sub Admin' : 'Super Admin'})</span>
              <div className="profile-circle">{currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : 'A'}</div>
            </div>
          </div>
        </header>

        <main className="admin-content">
          {activeTab === 'dashboard' && (
            <DashboardHome 
              orders={orders} 
              paymentRequests={paymentRequests} 
              counts={counts} 
              loading={loading} 
            />
          )} 
          {activeTab === 'enquiries' && <Enquiries />}
          {activeTab === 'services' && <AddServices />}
          {activeTab === 'items' && <Items />}
          {activeTab === 'orders' && <Orders />}
          {activeTab === 'expressOrders' && <ExpressOrders />}
          {activeTab === 'customers' && <ListUsers />}
          {activeTab === 'agents' && <ListAgents />}
          {activeTab === 'paymentRequests' && <PaymentRequests />}
          {activeTab === 'agentInvoices' && <AgentOutstanding />}
          {activeTab === 'accounts' && (isSuperAdmin ? <AccountsDashboard /> : <div style={{ padding: '24px', color: '#dc2626' }}>Access Denied: You do not have permission to view Accounts.</div>)}
        </main>
      </main>
    </div>
  );
}

// 🎯 DASHBOARD HOME COMPONENT
function DashboardHome({ orders, paymentRequests, counts, loading }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());

  const getOrderStatusCount = (statusName) => {
    return orders.filter(
      (o) => (o.orderStatus || '').toLowerCase() === statusName.toLowerCase()
    ).length;
  };

  const getPaymentStatusCount = (statusName) => {
    return paymentRequests.filter(
      (p) => (p.status || '').toLowerCase() === statusName.toLowerCase()
    ).length;
  };

  const daysInMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  ).getDay();

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const isSameDay = (d1, d2) => {
    return (
      d1.getDate() === d2.getDate() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getFullYear() === d2.getFullYear()
    );
  };

  const filteredOrdersByPickup = orders.filter((order) => {
    const pickupDateStr = order.scheduling?.pickup?.date;
    if (!pickupDateStr) return false;

    const pickupDate = new Date(pickupDateStr);
    return isSameDay(pickupDate, selectedDate);
  });

  if (loading) {
    return <div className="dashboard-loader">Loading Dashboard Overview...</div>;
  }

  return (
    <div className="dashboard-home-container">
      <div className="stats-main-content">
        
        {/* Express Orders Breakdown */}
        <div className="stats-section">
          <h3 className="section-title"><i className="fas fa-truck-loading"></i> Express Orders Overview</h3>
          <div className="stats-grid">
            <div className="stat-card card-blue">
              <div className="stat-icon"><i className="fas fa-box-open"></i></div>
              <div className="stat-info">
                <h4>Total Orders</h4>
                <p>{orders.length}</p>
              </div>
            </div>
            <div className="stat-card card-amber">
              <div className="stat-icon"><i className="fas fa-clock"></i></div>
              <div className="stat-info">
                <h4>Pending Orders</h4>
                <p>{getOrderStatusCount('Pending')}</p>
              </div>
            </div>
            <div className="stat-card card-sky">
              <div className="stat-icon"><i className="fas fa-user-check"></i></div>
              <div className="stat-info">
                <h4>Assigned Orders</h4>
                <p>{getOrderStatusCount('Assigned')}</p>
              </div>
            </div>
            <div className="stat-card card-teal">
              <div className="stat-icon"><i className="fas fa-thumbs-up"></i></div>
              <div className="stat-info">
                <h4>Accepted Orders</h4>
                <p>{getOrderStatusCount('Accepted')}</p>
              </div>
            </div>
            <div className="stat-card card-indigo">
              <div className="stat-icon"><i className="fas fa-hands"></i></div>
              <div className="stat-info">
                <h4>Picked Up</h4>
                <p>{getOrderStatusCount('Picked Up')}</p>
              </div>
            </div>
            <div className="stat-card card-purple">
              <div className="stat-icon"><i className="fas fa-shipping-fast"></i></div>
              <div className="stat-info">
                <h4>In-Transit</h4>
                <p>{getOrderStatusCount('In-Transit')}</p>
              </div>
            </div>
            <div className="stat-card card-green">
              <div className="stat-icon"><i className="fas fa-check-circle"></i></div>
              <div className="stat-info">
                <h4>Delivered</h4>
                <p>{getOrderStatusCount('Delivered')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Application Data */}
        <div className="stats-section">
          <h3 className="section-title"><i className="fas fa-cubes"></i> Application Data</h3>
          <div className="stats-grid">
            <div className="stat-card card-cyan">
              <div className="stat-icon"><i className="fas fa-users"></i></div>
              <div className="stat-info">
                <h4>Total Customers</h4>
                <p>{counts.users}</p>
              </div>
            </div>
            <div className="stat-card card-indigo">
              <div className="stat-icon"><i className="fas fa-id-badge"></i></div>
              <div className="stat-info">
                <h4>Total Agents</h4>
                <p>{counts.agents}</p>
              </div>
            </div>
            <div className="stat-card card-teal">
              <div className="stat-icon"><i className="fas fa-concierge-bell"></i></div>
              <div className="stat-info">
                <h4>Active Services</h4>
                <p>{counts.services}</p>
              </div>
            </div>
            <div className="stat-card card-sky">
              <div className="stat-icon"><i className="fas fa-tshirt"></i></div>
              <div className="stat-info">
                <h4>Active Items</h4>
                <p>{counts.items}</p>
              </div>
            </div>
            <div className="stat-card card-emerald">
              <div className="stat-icon"><i className="fas fa-envelope-open-text"></i></div>
              <div className="stat-info">
                <h4>Total Enquiries</h4>
                <p>{counts.enquiries}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Requests Breakdown */}
        <div className="stats-section">
          <h3 className="section-title"><i className="fas fa-wallet"></i> Payment Requests Overview</h3>
          <div className="stats-grid">
            <div className="stat-card card-blue">
              <div className="stat-icon"><i className="fas fa-receipt"></i></div>
              <div className="stat-info">
                <h4>Total Requests</h4>
                <p>{paymentRequests.length}</p>
              </div>
            </div>
            <div className="stat-card card-amber">
              <div className="stat-icon"><i className="fas fa-hourglass-half"></i></div>
              <div className="stat-info">
                <h4>Amount Pending</h4>
                <p>{getPaymentStatusCount('Amount pending')}</p>
              </div>
            </div>
            <div className="stat-card card-sky">
              <div className="stat-icon"><i className="fas fa-spinner"></i></div>
              <div className="stat-info">
                <h4>Processing</h4>
                <p>{getPaymentStatusCount('Processing')}</p>
              </div>
            </div>
            <div className="stat-card card-green">
              <div className="stat-icon"><i className="fas fa-money-check-alt"></i></div>
              <div className="stat-info">
                <h4>Paid Requests</h4>
                <p>{getPaymentStatusCount('Paid')}</p>
              </div>
            </div>
            <div className="stat-card card-red">
              <div className="stat-icon"><i className="fas fa-times-circle"></i></div>
              <div className="stat-info">
                <h4>Rejected Requests</h4>
                <p>{getPaymentStatusCount('Request rejected')}</p>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* BOTTOM SECTION: PICKUPS & CALENDAR */}
      <div className="dashboard-bottom-section">
        
        {/* Left Side: Pickups */}
        <div className="pickup-orders-card">
          <h4>
            <i className="fas fa-calendar-day"></i> Pickups on {selectedDate.toDateString()}
          </h4>

          <div className="pickup-orders-list">
            {filteredOrdersByPickup.length > 0 ? (
              filteredOrdersByPickup.map((ord) => (
                <div key={ord.id} className="pickup-item">
                  <div className="pickup-header">
                    <span className="pickup-id">{ord.displayOrderId || ord.autoGeneratedId || ord.id}</span>
                    <span className="pickup-status-chip">{ord.orderStatus || 'Pending'}</span>
                  </div>
                  
                  <div className="pickup-user">
                    <strong>{ord.userName || ord.customerName || 'Customer'}</strong> ({ord.userPhone || ord.customerPhone || 'N/A'})
                  </div>

                  <div className="pickup-timing">
                    <div className="time-row pickup">
                      <i className="fas fa-arrow-circle-up"></i>
                      <span><strong>Pick Up:</strong> {ord.scheduling?.pickup?.date || 'N/A'} at {ord.scheduling?.pickup?.time || 'N/A'}</span>
                    </div>
                    <div className="time-row dropoff">
                      <i className="fas fa-arrow-circle-down"></i>
                      <span><strong>Drop Off:</strong> {ord.scheduling?.dropoff?.date || 'N/A'} at {ord.scheduling?.dropoff?.time || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="no-pickups-msg">No pickup orders scheduled for this date.</p>
            )}
          </div>
        </div>

        {/* Right Side: Calendar */}
        <div className="calendar-card">
          <div className="calendar-header">
            <button onClick={handlePrevMonth} className="cal-nav-btn">&lt;</button>
            <h4>
              {currentDate.toLocaleString('default', { month: 'long' })} {currentDate.getFullYear()}
            </h4>
            <button onClick={handleNextMonth} className="cal-nav-btn">&gt;</button>
          </div>

          <div className="calendar-weekdays">
            <span>Sun</span><span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span>
          </div>

          <div className="calendar-days-grid">
            {Array.from({ length: firstDayOfMonth }).map((_, i) => (
              <div key={`empty-${i}`} className="cal-day empty"></div>
            ))}

            {Array.from({ length: daysInMonth }).map((_, i) => {
              const dayNumber = i + 1;
              const dateObj = new Date(currentDate.getFullYear(), currentDate.getMonth(), dayNumber);
              const isSelected = isSameDay(dateObj, selectedDate);
              const isToday = isSameDay(dateObj, new Date());

              return (
                <button
                  key={dayNumber}
                  className={`cal-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
                  onClick={() => setSelectedDate(dateObj)}
                >
                  {dayNumber}
                </button>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}

export default AdminDashboard;